import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, SparklesOutline, Stack, useDisclosure } from '@bonusly/design-system';

import { useSpotlightEligibility } from '../../../../../queries/employee-spotlights';
import { AwardType, Bonus } from '../../../../../types/bonus';
import { ConfirmationModal } from '../common/modal/confirmation-modal';

type Props = {
	bonus: Bonus;
};

const EMPLOYEE_SPOTLIGHT_CATEGORY = 'employee_spotlight';

const getWorkAnniversaryReceiver = (bonus: Bonus): Bonus['receivers'][number] | undefined => {
	if (bonus.award_type !== AwardType.WorkAnniversary) return undefined;
	const receivers = bonus.receivers ?? [];
	if (receivers.length !== 1) return undefined;
	return receivers[0];
};

const getFirstNameFromDisplayName = (displayName?: string): string | undefined => {
	const name = displayName?.trim();
	if (!name) return undefined;
	return name.split(/\s+/)[0];
};

const hasEmployeeSpotlightAttachment = (bonus: Bonus): boolean => {
	const hasOnBonus = (bonus.attachments ?? []).some((attachment) => attachment?.category === EMPLOYEE_SPOTLIGHT_CATEGORY);
	if (hasOnBonus) return true;

	// Employee snapshot attachments may be present on comment/add-on bonuses in the thread.
	return (bonus.child_bonuses ?? []).some((child) => hasEmployeeSpotlightAttachment(child));
};

export const WorkAnniversarySnapshotCTA: FC<Props> = ({ bonus }) => {
	const { t } = useTranslation('spotlight');
	const confirmModal = useDisclosure();
	const isWorkAnniversary = bonus.award_type === AwardType.WorkAnniversary;
	const bonusOrCommentsAlreadyHaveSnapshot = useMemo(() => hasEmployeeSpotlightAttachment(bonus), [bonus]);

	const receiver = useMemo(() => getWorkAnniversaryReceiver(bonus), [bonus]);

	const receiverFirstName = useMemo(() => {
		return getFirstNameFromDisplayName(receiver?.display_name);
	}, [receiver?.display_name]);

	const startTime = receiver?.hired_on ?? '';
	const endTime = bonus.created_at ?? '';

	const shouldQueryEligibility = Boolean(isWorkAnniversary && receiver?.id && startTime && endTime && !bonusOrCommentsAlreadyHaveSnapshot);

	const eligibility = useSpotlightEligibility(receiver?.id ?? '', startTime, endTime, {
		enabled: shouldQueryEligibility,
		staleTime: Infinity, // Don't refetch eligibility unless the user explicitly requests a new snapshot.
	});

	// TODO: Only show the CTA to the user's manager or an admin
	const showCTA = Boolean(isWorkAnniversary && receiver?.id && startTime && endTime && eligibility.data?.eligible && !bonusOrCommentsAlreadyHaveSnapshot);

	const onConfirm = useCallback(() => {
		confirmModal.onClose();
		// eslint-disable-next-line no-console
		console.log({
			event: 'work_anniversary_employee_snapshot_requested',
			bonusId: bonus.id,
			userId: receiver?.id,
			startTime,
			endTime,
		});
	}, [bonus.id, confirmModal, endTime, receiver?.id, startTime]);

	if (!showCTA) {
		return null;
	}

	return (
		<>
			<Stack pt={3}>
				<Button
					width="100%"
					leftIcon={<SparklesOutline size="md" />}
					onClick={() => confirmModal.onOpen()}
					bg="primary.bgAlphaHover"
					color="text.interactive"
					_hover={{ bg: 'primary.bgAlphaActive' }}
					_active={{ bg: 'primary.bgAlphaActive' }}
				>
					{receiverFirstName ? t('workAnniversaryCta.buttonWithName', { name: receiverFirstName }) : t('workAnniversaryCta.button')}
				</Button>
			</Stack>

			<ConfirmationModal
				blockScrollOnMount={false}
				confirmText={t('workAnniversaryCta.confirmButton')}
				description={t('workAnniversaryCta.confirmDescription')}
				header={t('workAnniversaryCta.confirmHeader')}
				isOpen={confirmModal.isOpen}
				onClose={confirmModal.onClose}
				onConfirm={onConfirm}
			/>
		</>
	);
};
